import * as d3 from 'd3';
import { interpolateSinebow } from 'd3-scale-chromatic';
import { Canvg } from 'canvg';
import { BaseChart, UIState } from '../../js/classes/BaseChart.ts';
import { CwcDataSet } from '../../js/generated/cwc_data.pb.js';

// D3セレクションの型エイリアスを定義
type D3Selection<T extends d3.BaseType> = d3.Selection<T, unknown, null, undefined>;

/**
 * 描画用の点データの型 (X軸がelapsedMonthsである点は変更なし)
 */
interface PointData {
	elapsedMonths: number;
	value: number;
}

/**
 * 描画用の系列データの型 (変更なし)
 */
interface LineSeries {
	key: string;
	color: string;
	points: PointData[];
	startIndex: number;
}

// メタデータ型定義
interface GameConsoleMetaData {
	timeline: string[];
	consoles: { [key: string]: ConsoleInfo };
	seriesOrder: string[];
}
interface ConsoleInfo {
	name: string;
}


/**
 * ゲーム機販売台数推移グラフ (GameConsoleChart)
 * BaseChartを継承し、Canvas/SVGハイブリッド描画を行う。
 */
export class GameConsoleChart extends BaseChart<CwcDataSet, LineSeries> {
	// --- 静的プロパティ ---
	static override defaultConfig = {
		defaultVisibleKeys: ['FC', 'SFC', 'N64', 'GC', 'Wii', 'WiiU', 'NS', 'NS2'],
		animationDurationPerSeries: 2000,
		autoplay: true,
		autoplayDelay: 500,
		lineWidth: 2.5,
		gridLineWidth: 1,
		highlightOpacity: 1.0,
		dimmedOpacity: 0.2,
		xAxisTickIntervalMonths: 24,
		yAxisTicks: 5,
		yAxisTickPadding: 8,
		axisLabelOffsetY: 20,
		tooltipOffsetX: -100,
		tooltipOffsetY: -100,
	};

	// --- プロパティ定義 ---
	private lineData: LineSeries[] = [];
	private seriesOrder: string[] = [];
	private visibleSeriesKeys: Set<string>;

	// Protobuf/Mapping.jsonのデータを保持するプロパティ
	protected override fullDataSet: CwcDataSet | null = null;
	private mappingData: GameConsoleMetaData | null = null;

	// 描画関連プロパティ
	private lineCanvas: D3Selection<HTMLCanvasElement> | null = null;
	private lineCtx: CanvasRenderingContext2D | null = null;
	private xScale: d3.ScaleLinear<number, number> | null = null;
	private yScale: d3.ScaleLinear<number, number> | null = null;
	private colorScale: d3.ScaleOrdinal<string, string> | null = null;
	private lineGenerator: d3.Line<PointData> | null = null;

	private timeline: string[] = [];
	private markersGroup: D3Selection<SVGGElement> | null = null;
	private highlightedKey: string | null = null;
	private tooltip: D3Selection<HTMLDivElement> | null = null;
	private hoverGridLinesGroup: D3Selection<SVGGElement> | null = null;

	constructor(chartElement: HTMLElement, config = {}) {
		super(chartElement, config);
		this.visibleSeriesKeys = new Set(this.config.defaultVisibleKeys);
		this.margin = { top: 20, right: 20, bottom: 50, left: 70 };
	}

	/**
	 * UI構築用データ（凡例等）を読み込む
	 * @returns {Promise<UiDataForInitializer | null>}
	 */
	public async loadDataForUI(): Promise<UiDataForInitializer | null> {
		if (this.mappingData) {
			return { seriesOrder: this.mappingData.seriesOrder, consoles: this.mappingData.consoles };
		}

		// Role属性ベースのコンテナからdata属性を取得
		const mappingUrl = (this.chartElement as HTMLElement).dataset.mappingJsonSourceUrl;
		const protoUrl = (this.chartElement as HTMLElement).dataset.protobufSourceUrl;

		if (!mappingUrl || !protoUrl) {
			this._displayMessage('データソースのURLが指定されていません。', 'error');
			return null;
		}

		try {
			const [protoResponse, mappingResponse] = await Promise.all([
				fetch(protoUrl),
				fetch(mappingUrl)
			]);
			if (!protoResponse.ok) throw new Error(`Protobufの読み込みに失敗: ${protoResponse.statusText}`);
			if (!mappingResponse.ok) throw new Error(`マッピングJSONの読み込みに失敗: ${mappingResponse.statusText}`);

			const [protoBuffer, mappingJson] = await Promise.all([
				protoResponse.arrayBuffer(),
				mappingResponse.json()
			]);

			const decodedMessage = CwcDataSet.decode(new Uint8Array(protoBuffer));
			this.fullDataSet = CwcDataSet.toObject(decodedMessage, { defaults: true }) as CwcDataSet;
			this.mappingData = mappingJson.meta as GameConsoleMetaData;

			return { seriesOrder: this.mappingData.seriesOrder, consoles: this.mappingData.consoles };
		} catch (error) {
			console.error('GameConsoleChart CRITICAL ERROR: Failed to load or parse data.', { error });
			this._displayMessage('データファイルの読み込みに失敗しました。', 'error');
			return null;
		}
	}

	// initメソッドをconsole-wars-chartと同様の正しいライフサイクルに修正
	public override async init(): Promise<void> {
		// データがまだ読み込まれていなければ、UI生成を伴わない純粋なデータロードを行う
		if (!this.fullDataSet) {
			await this.loadDataForUI();
		}

		// BaseChartのinitを呼び出す
		await super.init();
	}

	public setVisibleSeries(visibleKeys: Set<string>): void {
		this.visibleSeriesKeys = visibleKeys;
		this.resetAndRedraw();
	}

	protected override _processData(): void {
		if (!this.fullDataSet || !this.mappingData) return;

		this.seriesOrder = this.mappingData.seriesOrder;

		this.colorScale = d3.scaleOrdinal<string, string>()
			.domain(this.seriesOrder)
			.range(this.seriesOrder.map((_, i) => interpolateSinebow(i / (this.seriesOrder.length - 1))));

		this.timeline = this.mappingData.timeline;

		this.lineData = this.fullDataSet.series.reduce<LineSeries[]>((acc, seriesProto) => {
			const id = seriesProto.id;
			const values = seriesProto.values;

			if (id == null || !Array.isArray(values) || id < 0 || id >= this.seriesOrder.length) {
				return acc;
			}
			const key = this.seriesOrder[id];

			// X軸の値を「経過月数（インデックス）」とする
			const points: PointData[] = values.map((value, index) => ({
				elapsedMonths: index,
				value: value
			}));

			acc.push({
				key,
				color: this.colorScale!(key),
				points,
				startIndex: seriesProto.startIndex ?? 0
			});
			return acc;
		}, []);
	}

	protected override resetAndRedraw(): void {
		this.pause();
		this.currentFrame = 0;

		this.activeLineData = this.seriesOrder
			.filter(key => this.visibleSeriesKeys.has(key))
			.map(key => this.lineData.find(d => d.key === key))
			.filter((d): d is LineSeries => !!d);

		this._initialDrawAxes();

		if (this.activeLineData.length === 0) {
			this.totalFrames = 0;
			this.lineCtx?.clearRect(0, 0, this.lineCanvas!.node()!.width, this.lineCanvas!.node()!.height);
			this.updateUI();
			return;
		}

		const framesPerSeries = (this.config.animationDurationPerSeries / 1000) * 60;
		this.totalFrames = framesPerSeries * this.activeLineData.length;
		this.isReady = true;

		this.drawFrame(0);
		this.updateUI();

		if (this.config.autoplay) {
			setTimeout(() => this.play(), this.config.autoplayDelay);
		}
	}

	protected override setupLayers(): void {
		d3.select(this.chartElement).select('canvas').remove();
		d3.select(this.chartElement).select('svg').remove();

		const containerRect = this.chartElement.getBoundingClientRect();
		const totalWidth = containerRect.width;
		const totalHeight = containerRect.height;

		this.lineCanvas = d3.select(this.chartElement).append('canvas')
			.style('width', `${totalWidth}px`).style('height', `${totalHeight}px`);
		this.lineCtx = this.lineCanvas.node()!.getContext('2d');
		const dpr = window.devicePixelRatio || 1;
		this.lineCanvas.node()!.width = totalWidth * dpr;
		this.lineCanvas.node()!.height = totalHeight * dpr;
		this.lineCtx!.scale(dpr, dpr);

		this.svg = d3.select(this.chartElement).append('svg')
			.attr('width', totalWidth).attr('height', totalHeight)
			.attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
			.on('mouseleave', () => {
				if (!this.isPlaying) {
					this.highlightedKey = null;
					if (this.tooltip) this.tooltip.classed('is-visible', false);
					if (this.hoverGridLinesGroup) this.hoverGridLinesGroup.selectAll('*').remove();
					this.drawFrame(this.currentFrame);
				}
			});

		this.g = this.svg.append('g').attr('transform', `translate(${this.margin.left},${this.margin.top})`);

		// Role属性ベースでラッパー要素を探索
		const wrapper = this.chartElement.closest<HTMLElement>('[data-chart-role="container"]');
		if (wrapper) {
			// 既存のツールチップがあれば取得、なければ作成
			let tooltip = d3.select(wrapper).select<HTMLDivElement>('.chart-tooltip');
			if (tooltip.empty()) {
				tooltip = d3.select(wrapper).append('div')
					.attr('class', 'chart-tooltip')
					.attr('data-chart-role', 'tooltip');
			}
			this.tooltip = tooltip;
		}
	}

	protected override _initialDrawAxes(): void {
		if (!this.g || !this.mappingData) return;

		this.g.selectAll('*').remove();
		this.hoverGridLinesGroup = this.g.append('g').attr('class', 'hover-grid-lines');

		const activeMaxElapsedMonths = d3.max(this.activeLineData, series => d3.max(series.points, d => d.elapsedMonths)) || 0;
		this.xScale = d3.scaleLinear().domain([0, activeMaxElapsedMonths]).range([0, this.width]);

		const activeYMax = d3.max(this.activeLineData, series => d3.max(series.points, d => d.value)) || 0;
		this.yScale = d3.scaleLinear().domain([0, activeYMax]).range([this.height, 0]).nice();

		this.lineGenerator = d3.line<PointData>().x(d => this.xScale!(d.elapsedMonths)).y(d => this.yScale!(d.value));

		const yearTicks = d3.range(0, activeMaxElapsedMonths + 1, this.config.xAxisTickIntervalMonths);
		const xAxis = d3.axisBottom(this.xScale).tickValues(yearTicks).tickFormat(d => `${(d as number) / 12}年`).tickSizeOuter(0);
		this.g.append('g').attr('class', 'x-axis axis').attr('transform', `translate(0, ${this.height})`).call(xAxis);

		const yAxis = d3.axisLeft(this.yScale)
			.ticks(this.config.yAxisTicks)
			.tickFormat(d => `${(d as number) / 1_000_000}M`)
			.tickPadding(this.config.yAxisTickPadding);
		this.g.append('g').attr('class', 'y-axis axis').call(yAxis);

		this.markersGroup = this.g.append('g').attr('class', 'markers-group');

		this.g.append('text').attr('class', 'axis-label').attr('transform', 'rotate(-90)').attr('y', 0 - this.margin.left).attr('x', 0 - this.height / 2).attr('dy', '1em').text('普及台数');
		this.g.append('text').attr('class', 'axis-label').attr('transform', `translate(${this.width / 2}, ${this.height + this.margin.top + this.config.axisLabelOffsetY})`).text('販売期間');
	}

	protected override async drawFrame(frame: number): Promise<void> {
		await this._redrawCanvas(frame);
		this._redrawSVG(frame);
	}

	public override redrawOnResize(): void {
		this.pause();
		this.init();
	}

	private async _redrawCanvas(frame: number): Promise<void> {
		if (!this.lineCtx || !this.lineCanvas || !this.yScale) return;
		const ctx = this.lineCtx;
		const canvas = this.lineCanvas.node()!;

		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.save();
		ctx.translate(this.margin.left, this.margin.top);

		const gridTicks = this.yScale.ticks(this.config.yAxisTicks);

		ctx.strokeStyle = '#e9e9e9';
		ctx.lineWidth = this.config.gridLineWidth;
		gridTicks.forEach(tick => {
			if (tick === 0) return;
			ctx.beginPath();
			ctx.moveTo(0, this.yScale!(tick));
			ctx.lineTo(this.width, this.yScale!(tick));
			ctx.stroke();
		});

		if (this.activeLineData.length === 0) {
			ctx.restore();
			return;
		}

		const gForRender = document.createElementNS('http://www.w3.org/2000/svg', 'g');
		const framesPerSeries = this.totalFrames / this.activeLineData.length;

		for (let i = 0; i < this.activeLineData.length; i++) {
			const series = this.activeLineData[i];
			const startFrame = i * framesPerSeries;
			const endFrame = (i + 1) * framesPerSeries;

			let progress = 0;
			if (frame >= endFrame - 1) progress = 1;
			else if (frame > startFrame) progress = (frame - startFrame) / (framesPerSeries - 1);
			else continue;

			if (progress > 0) {
				const interpolatedPoints = this._getInterpolatedPoints(series.points, progress);
				if (interpolatedPoints.length < 2 || !this.lineGenerator) continue;

				const pathData = this.lineGenerator(interpolatedPoints);
				if (pathData) {
					const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
					const isHighlighted = this.highlightedKey === series.key;
					const isHighlighting = this.highlightedKey !== null;
					const strokeOpacity = !isHighlighting || isHighlighted ? this.config.highlightOpacity : this.config.dimmedOpacity;

					const rgbaColor = d3.color(series.color) as d3.RGBColor;
					if (rgbaColor) {
						rgbaColor.opacity = strokeOpacity;
						path.setAttribute('stroke', rgbaColor.toString());
					}

					path.setAttribute('d', pathData);
					path.setAttribute('stroke-width', this.config.lineWidth.toString());
					path.setAttribute('stroke-linecap', 'round');
					path.setAttribute('stroke-linejoin', 'round');
					path.setAttribute('fill', 'none');
					gForRender.appendChild(path);
				}
			}
		}

		if (gForRender.hasChildNodes()) {
			try {
				const v = await Canvg.fromString(ctx, gForRender.outerHTML, { ignoreClear: true });
				await v.render();
			} catch (e) { console.error(e); }
		}
		ctx.restore();
	}

	private _redrawSVG(frame: number): void {
		if (!this.markersGroup || !this.xScale || !this.yScale || !this.mappingData) return;

		if (frame < this.totalFrames - 1) {
			this.markersGroup.selectAll('*').remove();
			return;
		}

		this.markersGroup.selectAll('*').remove();
		this.activeLineData.forEach(series => {
			const lastPoint = series.points[series.points.length - 1];
			if (lastPoint) {
				const cx = this.xScale!(lastPoint.elapsedMonths);
				const cy = this.yScale!(lastPoint.value);

				const consoleInfo = this.mappingData!.consoles[series.key];
				const consoleName = consoleInfo?.name || series.key.toUpperCase();

				this.markersGroup!.append('circle')
					.attr('class', 'endpoint-marker')
					.attr('cx', cx).attr('cy', cy)
					.attr('r', 5)
					.style('fill', series.color)
					.attr('data-key', series.key)
					.attr('data-name', consoleName) // data属性にも正式名称をセット
					.attr('data-value', lastPoint.value)
					.attr('data-months', lastPoint.elapsedMonths)
					.on('mouseover', (event: MouseEvent) => {
						if (this.isPlaying) return;
						const targetElement = event.currentTarget as SVGCircleElement;
						const target = d3.select(targetElement);

						target.classed('is-hovered', true);
						this.highlightedKey = target.attr('data-key');
						this._redrawCanvas(this.currentFrame);

						this.hoverGridLinesGroup?.selectAll('*').remove();
						this.hoverGridLinesGroup?.append('line').attr('x1', cx).attr('y1', cy).attr('x2', cx).attr('y2', this.height);
						this.hoverGridLinesGroup?.append('line').attr('x1', cx).attr('y1', cy).attr('x2', 0).attr('y2', cy);
						this.tooltip?.classed('is-visible', true);
					})
					.on('mousemove', (event: MouseEvent) => {
						if (this.isPlaying || !this.tooltip) return;
						const target = d3.select(event.currentTarget as SVGCircleElement);

						const key = target.attr('data-key');
						if (!key) return;

						const consoleData = this.mappingData?.consoles[key];
						const name = consoleData?.name || key.toUpperCase();
						const releaseDate = consoleData?.releaseDate;

						const value = parseFloat(target.attr('data-value')).toLocaleString();
						const months = target.attr('data-months');

						let tooltipHtml = `<div class="tooltip-name">${name}</div>`;
						if (releaseDate) {
							tooltipHtml += `<div class="tooltip-release-date">発売日: ${releaseDate}</div>`;
						}
						tooltipHtml += `<div class="tooltip-value">${value} <span class="unit">units</span></div>`;
						tooltipHtml += `<div class="tooltip-months">${months} <span class="unit">months</span></div>`;

						this.tooltip.html(tooltipHtml);

						const wrapper = this.chartElement.closest<HTMLElement>('[data-chart-role="container"]');
						if (wrapper) {
							const [mouseX, mouseY] = d3.pointer(event, wrapper);
							this.tooltip.style('left', `${mouseX + this.config.tooltipOffsetX}px`).style('top', `${mouseY + this.config.tooltipOffsetY}px`);
						}
					})
					.on('mouseout', (event: MouseEvent) => {
						if (this.isPlaying) return;
						d3.select(event.currentTarget as SVGCircleElement).classed('is-hovered', false);
						this.highlightedKey = null;
						this.hoverGridLinesGroup?.selectAll('*').remove();
						this.tooltip?.classed('is-visible', false);
						this._redrawCanvas(this.currentFrame);
					});
			}
		});
	}

	private _getInterpolatedPoints(points: PointData[], progress: number): PointData[] {
		const totalLength = (points.length - 1) * progress;
		const currentIndex = Math.floor(totalLength);
		const fraction = totalLength - currentIndex;
		const newPoints = points.slice(0, currentIndex + 1);

		if (currentIndex < points.length - 1 && fraction > 0) {
			const p1 = points[currentIndex];
			const p2 = points[currentIndex + 1];
			newPoints.push({
				elapsedMonths: p1.elapsedMonths + (p2.elapsedMonths - p1.elapsedMonths) * fraction,
				value: p1.value + (p2.value - p1.value) * fraction,
			});
		}
		return newPoints;
	}
}